import React, { useEffect, useState } from "react";
import { CheckCircle, Circle, Robot, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";

const GRADE_COLOR = {
  A: "text-emerald-600", B: "text-emerald-600", C: "text-amber-500",
  D: "text-orange-500", F: "text-red-600",
};
const RING_COLOR = {
  A: "#059669", B: "#10b981", C: "#f59e0b", D: "#f97316", F: "#dc2626",
};

export default function AiReadiness({ businessId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let active = true;
    api.get(`/businesses/${businessId}/ai-readiness`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [businessId, refreshKey]);

  if (!data) {
    return <div className="mt-6 h-28 rounded-2xl border border-slate-200 bg-slate-50 animate-pulse" />;
  }

  const score = data.score;
  const color = RING_COLOR[data.grade] || "#0055FF";
  const circumference = 2 * Math.PI * 34;
  const dash = (score / 100) * circumference;
  const todos = data.factors.filter((f) => !f.done);

  return (
    <div data-testid="ai-readiness-card" className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="relative h-24 w-24 shrink-0" data-testid="ai-readiness-score">
          <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#eef2f7" strokeWidth="8" />
            <circle cx="40" cy="40" r="34" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference}`} style={{ transition: "stroke-dasharray 0.8s ease" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-2xl font-black text-slate-900">{score}</span>
            <span className={`text-xs font-bold ${GRADE_COLOR[data.grade]}`}>Grade {data.grade}</span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[#0055FF]">
            <Robot weight="fill" size={18} />
            <p className="text-xs font-bold uppercase tracking-[0.12em]">AI Readiness Score</p>
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900 mt-1.5">{data.summary}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {todos.length === 0
              ? "All checks passed — you're fully optimized."
              : `${todos.length} fix-it step${todos.length > 1 ? "s" : ""} to boost your visibility across AI and directories.`}
          </p>
        </div>

        <button onClick={() => setOpen((o) => !o)} data-testid="ai-readiness-toggle"
          className="self-start sm:self-center flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
          {open ? "Hide steps" : "Show steps"}
          <CaretDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {data.factors.map((f) => (
            <div key={f.key} data-testid={`ai-factor-${f.key}`}
              className="px-6 sm:px-7 py-3.5 flex items-start gap-3">
              {f.done
                ? <CheckCircle weight="fill" size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                : <Circle size={20} className="text-slate-300 shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${f.done ? "text-slate-900" : "text-slate-700"}`}>{f.label}</p>
                {!f.done && <p className="text-sm text-slate-500 mt-0.5">{f.tip}</p>}
              </div>
              <span className="ml-auto text-xs font-bold text-slate-400 shrink-0">{f.weight} pts</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
